// ===== CSS & Global Styles =====
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '@fancyapps/ui/dist/fancybox.css';
import '@fancyapps/ui/dist/carousel.css';
import 'sweetalert2/dist/sweetalert2.min.css';
import Swal from 'sweetalert2';
window.Swal = Swal;
import '../sass/style.scss';

// ===== JS Helpers =====
import './cache-buster';
import './validation';
import './cropper';
import { initFileUploadValidation } from './file-upload-validation';
import { initYouTubePreview } from './youtube';

// ===== Components =====
import './tooltip';
import './carousel';
import './infinite-grid';
import { initLinePopup } from './line-popup';

function initSmartMediaAspect() {
  const mediaNodes = document.querySelectorAll('.js-smart-media');

  mediaNodes.forEach((img) => {
    const applyRatio = () => {
      const parentItem = img.closest('.section-media__item--feature');
      if (!parentItem) return;

      const width = img.naturalWidth || 0;
      const height = img.naturalHeight || 0;
      const fallbackRatio = img.dataset.defaultRatio || '16:9';

      if (!width || !height) {
        parentItem.classList.toggle('is-square', fallbackRatio === '1:1');
        return;
      }

      const ratio = width / height;
      parentItem.classList.toggle('is-square', ratio <= 1.1);
    };

    if (img.complete) {
      applyRatio();
    } else {
      img.addEventListener('load', applyRatio, { once: true });
    }
  });
}

function initMarketplaceMediaCards() {
  const mediaImages = Array.from(document.querySelectorAll('.mis-photo img'));
  if (!mediaImages.length) return;

  const markLoaded = (img) => {
    const card = img.closest('.mis-photo');
    if (!card) return;
    card.classList.remove('is-loading');
    card.classList.add('is-loaded');
  };

  const applyOrientation = (img) => {
    const card = img.closest('.mis-photo');
    if (!card) return;

    const width = Number(img.naturalWidth || 0);
    const height = Number(img.naturalHeight || 0);

    card.classList.remove('is-landscape', 'is-portrait', 'is-square', 'is-pending');

    if (!width || !height) return false;

    const src = (img.currentSrc || img.src || '').replace(/"/g, '\\"');
    if (src) {
      card.style.setProperty('--photo-bg', `url("${src}")`);
    }

    const ratio = width / height;

    if (ratio >= 1.05) {
      card.classList.add('is-landscape');
    } else if (ratio <= 0.95) {
      card.classList.add('is-portrait');
    } else {
      card.classList.add('is-square');
    }

    card.classList.add('is-loaded');
    return true;
  };

  const finalizeImage = (img) => {
    applyOrientation(img);
    markLoaded(img);
  };

  const watchImage = (img) => {
    const card = img.closest('.mis-photo');
    if (!card) return;

    card.classList.add('is-loading');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'low');

    const onReady = () => {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => null).finally(() => finalizeImage(img));
      } else {
        finalizeImage(img);
      }
    };

    if (img.complete && img.naturalWidth > 0) {
      onReady();
      return;
    }

    img.addEventListener('load', onReady, { once: true });
    img.addEventListener('error', () => {
      card.classList.remove('is-loading');
      card.classList.add('is-error');
    }, { once: true });
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        observer.unobserve(img);
        watchImage(img);
      });
    }, {
      rootMargin: '300px 0px',
      threshold: 0.01,
    });

    mediaImages.forEach((img) => observer.observe(img));
  } else {
    mediaImages.forEach((img) => watchImage(img));
  }
}

function getStickyHeaderOffset() {
  const header = document.querySelector(
    '.header.sticky-top, .navbar.fixed-top, .sticky-header, header.sticky-top, .header'
  );
  const shell = document.querySelector('.scrollspy-shell');

  const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 0;
  const shellHeight = (shell && window.innerWidth >= 992)
    ? Math.round(shell.getBoundingClientRect().height)
    : 0;
  const gap = window.innerWidth >= 992 ? 20 : 14;

  const total = headerHeight + shellHeight + gap;

  document.documentElement.style.setProperty('--sticky-header-offset', `${headerHeight}px`);
  document.documentElement.style.setProperty('--scrollspy-total-offset', `${total}px`);

  return total;
}

function syncActiveLinks(targetId, navs) {
  navs.forEach((nav) => {
    const links = Array.from(nav.querySelectorAll('a[href^="#"]'));

    links.forEach((link) => {
      const isActive = link.getAttribute('href') === `#${targetId}`;
      link.classList.toggle('active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'true');
        if (!syncActiveLinks._fromScroll) {
          try {
            link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          } catch (error) {
            link.scrollIntoView({ block: 'nearest', inline: 'center' });
          }
        }
      } else {
        link.removeAttribute('aria-current');
      }
    });
  });
}

function initSmoothScrollSpy() {
  const navs = Array.from(document.querySelectorAll('#scrollspy, #scrollspy-mobile')).filter(Boolean);
  const sections = Array.from(document.querySelectorAll('[data-scrollspy-section]'));

  if (!navs.length || !sections.length) return;

  const allLinks = navs.flatMap((nav) => Array.from(nav.querySelectorAll('a[href^="#"]')));
  const targetIds = sections.map((section) => section.id);

  const updateScrollMargins = () => {
    const offset = getStickyHeaderOffset();
    document.querySelectorAll('.scrollspy-section').forEach((el) => {
      el.style.scrollMarginTop = `${offset}px`;
    });
  };

  allLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      const href = link.getAttribute('href') || '';
      const targetId = href.replace('#', '');
      const target = targetId ? document.getElementById(targetId) : null;

      if (!target) return;

      event.preventDefault();
      updateScrollMargins();

      const top = window.scrollY + target.getBoundingClientRect().top - getStickyHeaderOffset();
      window.scrollTo({ top, behavior: 'smooth' });
      syncActiveLinks(targetId, navs);
    });
  });

  const updateActiveSection = () => {
    const offset = getStickyHeaderOffset();
    let currentId = targetIds[0];

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top - offset <= 24) {
        currentId = section.id;
      }
    });

    if (currentId) syncActiveLinks(currentId, navs);
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      syncActiveLinks._fromScroll = true;
      updateActiveSection();
      syncActiveLinks._fromScroll = false;
      ticking = false;
    });
  };

  updateScrollMargins();
  updateActiveSection();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    updateScrollMargins();
    updateActiveSection();
  });
  window.addEventListener('load', () => {
    updateScrollMargins();
    updateActiveSection();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  try { initYouTubePreview?.(); } catch (e) { console.error('youtube init error', e); }
  try { initFileUploadValidation?.(); } catch (e) { console.error('file-upload-validation init error', e); }
  try { initLinePopup?.(); } catch (e) { console.error('line-popup init error', e); }

  const btnOtp = document.querySelector('.btn-sendotp');
  if (btnOtp) {
    import('./otp').then((m) => m.initOtp?.()).catch(console.error);
  }

  if (document.querySelector('[data-toggle="password"]')) {
    import('./password').then((m) => m.default?.()).catch(console.error);
  }

  initSmartMediaAspect();
  initMarketplaceMediaCards();
  initSmoothScrollSpy();
});